/**
 * Network Monitoring Tab - Admin Dashboard
 * PPS monitoring, alerts, VM audit log, and configuration
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Activity, AlertTriangle, Shield, Server, RefreshCw, Settings, CheckCircle, Search, Clock } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const NetworkTab = ({ token }) => {
  const [currentPPS, setCurrentPPS] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('dashboard');
  const [hours, setHours] = useState(1);
  const [auditSearch, setAuditSearch] = useState('');
  const [editConfig, setEditConfig] = useState(null);
  const [expandedAlerts, setExpandedAlerts] = useState(new Set());

  const toggleAlertExpand = (i) => setExpandedAlerts(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchAll = useCallback(async () => {
    try {
      const [ppsRes, metricsRes, alertsRes, configRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/pps/current`, { headers }),
        fetch(`${API_BASE}/api/admin/pps/metrics?hours=${hours}&interface=eno1`, { headers }),
        fetch(`${API_BASE}/api/admin/pps/alerts?limit=20`, { headers }),
        fetch(`${API_BASE}/api/admin/pps/config`, { headers }),
      ]);
      if (ppsRes.ok) setCurrentPPS((await ppsRes.json()).interfaces || []);
      if (metricsRes.ok) {
        const data = await metricsRes.json();
        setMetrics((data.metrics || []).map(m => ({
          ...m,
          time: new Date(m.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
        })));
      }
      if (alertsRes.ok) setAlerts((await alertsRes.json()).alerts || []);
      if (configRes.ok) {
        const c = await configRes.json();
        setConfig(c);
        if (!editConfig) setEditConfig(c);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [hours]);

  const fetchAudit = async () => {
    try {
      const url = auditSearch
        ? `${API_BASE}/api/admin/vm-audit?limit=100&user_email=${encodeURIComponent(auditSearch)}`
        : `${API_BASE}/api/admin/vm-audit?limit=100`;
      const res = await fetch(url, { headers });
      if (res.ok) setAuditLogs((await res.json()).logs || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchAll(); }, [hours]);
  useEffect(() => { if (view === 'audit') fetchAudit(); }, [view, auditSearch]);

  // Auto-refresh every 10 seconds - only on dashboard view (NOT config)
  useEffect(() => {
    if (view === 'config') return;
    const interval = setInterval(fetchAll, 10000);
    return () => clearInterval(interval);
  }, [hours, view]);

  const saveConfig = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/pps/config`, {
        method: 'PUT', headers,
        body: JSON.stringify(editConfig)
      });
      if (res.ok) {
        toast.success('Config saved');
        // Reload config from server to confirm
        const configRes = await fetch(`${API_BASE}/api/admin/pps/config`, { headers });
        if (configRes.ok) {
          const c = await configRes.json();
          setConfig(c);
          setEditConfig(c);
        }
      }
      else toast.error('Save failed');
    } catch { toast.error('Error'); }
  };

  const getPPSColor = (val) => {
    if (!config) return 'text-green-400';
    if (val >= (config.emergency_threshold || 25000)) return 'text-red-500';
    if (val >= (config.critical_threshold || 15000)) return 'text-red-400';
    if (val >= (config.warning_threshold || 5000)) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getLevelBadge = (level) => {
    const cls = { warning: 'bg-yellow-500/20 text-yellow-300', critical: 'bg-red-500/20 text-red-300', emergency: 'bg-red-600/30 text-red-200' };
    return <Badge className={cls[level] || 'bg-gray-500/20 text-gray-300'}>{level?.toUpperCase()}</Badge>;
  };

  const getActionBadge = (action) => {
    const cls = { start: 'bg-green-500/20 text-green-300', stop: 'bg-blue-500/20 text-blue-300', expired: 'bg-yellow-500/20 text-yellow-300', auto_killed: 'bg-red-500/20 text-red-300' };
    return <Badge className={cls[action] || 'bg-gray-500/20 text-gray-300'}>{action?.toUpperCase()}</Badge>;
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 text-teal-500 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="network-monitoring-tab">
      {/* Sub-navigation */}
      <div className="flex gap-2 flex-wrap">
        {['dashboard', 'audit', 'config'].map(v => (
          <Button key={v} size="sm" onClick={() => setView(v)}
            className={view === v ? 'bg-teal-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}>
            {v === 'dashboard' ? <><Activity className="w-4 h-4 mr-1" />Live Monitor</> :
             v === 'audit' ? <><Server className="w-4 h-4 mr-1" />VM Audit Log</> :
             <><Settings className="w-4 h-4 mr-1" />Config</>}
          </Button>
        ))}
        <Button size="sm" variant="outline" onClick={fetchAll} className="ml-auto border-white/20 text-white">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* ========== DASHBOARD VIEW ========== */}
      {view === 'dashboard' && (
        <>
          {/* Current PPS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentPPS.length > 0 ? currentPPS.map((iface, i) => (
              <Card key={i} className="bg-white/5 border-white/10">
                <CardContent className="p-4">
                  <p className="text-xs text-gray-400 mb-1">{iface.interface}</p>
                  <div className="flex items-end gap-4">
                    <div>
                      <p className="text-xs text-gray-500">RX</p>
                      <p className={`text-2xl font-bold ${getPPSColor(iface.rx_pps)}`}>{iface.rx_pps?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">TX</p>
                      <p className={`text-2xl font-bold ${getPPSColor(iface.tx_pps)}`}>{iface.tx_pps?.toLocaleString()}</p>
                    </div>
                    <span className="text-xs text-gray-600 ml-auto">PPS</span>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <Card className="bg-white/5 border-white/10 col-span-3">
                <CardContent className="p-6 text-center text-gray-500">
                  No PPS data yet. Monitoring active on production server.
                </CardContent>
              </Card>
            )}
          </div>

          {/* Graph */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-base">PPS Graph</CardTitle>
                <div className="flex gap-1">
                  {[1, 6, 24].map(h => (
                    <Button key={h} size="sm" onClick={() => setHours(h)}
                      className={hours === h ? 'bg-teal-600 text-white' : 'bg-white/10 text-gray-400'}>
                      {h}h
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="h-64">
              {metrics.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={metrics}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="time" stroke="#666" tick={{ fontSize: 10 }} />
                    <YAxis stroke="#666" tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid #333', color: '#fff' }} />
                    <Area type="monotone" dataKey="rx_pps" name="RX PPS" stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.1} />
                    <Area type="monotone" dataKey="tx_pps" name="TX PPS" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.1} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                  Graph data will appear when monitoring runs on production
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Alerts */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" />Recent Alerts</CardTitle>
                {alerts.length > 0 && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={async () => {
                      try {
                        const res = await fetch(`${API_BASE}/api/admin/pps/alerts/resolve-all`, { method: 'POST', headers });
                        if (res.ok) { toast.success('All alerts resolved'); fetchAll(); }
                      } catch { toast.error('Failed'); }
                    }} className="bg-green-600/20 text-green-300 hover:bg-green-600/30 text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" />Resolve All
                    </Button>
                    <Button size="sm" onClick={async () => {
                      if (!window.confirm('Delete all alert history?')) return;
                      try {
                        const res = await fetch(`${API_BASE}/api/admin/pps/alerts/clear`, { method: 'DELETE', headers });
                        if (res.ok) { toast.success('Alert history cleared'); fetchAll(); }
                      } catch { toast.error('Failed'); }
                    }} className="bg-red-600/20 text-red-300 hover:bg-red-600/30 text-xs">
                      Clear History
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {alerts.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {alerts.map((a, i) => {
                    const isSevere = a.level === 'critical' || a.level === 'emergency';
                    const isExpanded = expandedAlerts.has(i);
                    return (
                      <div key={i} className="rounded-lg overflow-hidden border border-white/5">
                        {/* Main alert row */}
                        <div className="flex items-center justify-between bg-white/5 px-3 py-2 text-sm">
                          <div className="flex items-center gap-3">
                            {getLevelBadge(a.level)}
                            <span className="text-gray-300">{a.interface}</span>
                            <span className={`font-bold ${a.level === 'emergency' ? 'text-red-400' : a.level === 'critical' ? 'text-red-300' : 'text-yellow-300'}`}>
                              {a.pps_value?.toLocaleString()} PPS
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {a.auto_action_taken && <span className="text-xs text-red-400">{a.auto_action_taken}</span>}
                            <Badge className={a.status === 'active' ? 'bg-red-500/20 text-red-300' : 'bg-green-500/20 text-green-300'}>
                              {a.status}
                            </Badge>
                            <span className="text-xs text-gray-500">{a.timestamp ? new Date(a.timestamp).toLocaleString('en-IN') : ''}</span>
                            {a.status === 'active' && (
                              <button onClick={async () => {
                                try {
                                  const res = await fetch(`${API_BASE}/api/admin/pps/alerts/${i}/resolve`, { method: 'POST', headers });
                                  if (res.ok) { toast.success('Resolved'); fetchAll(); }
                                } catch { toast.error('Failed'); }
                              }} className="text-xs text-green-400 hover:text-green-300 underline ml-1">Resolve</button>
                            )}
                            {/* Expand toggle — only for critical / emergency */}
                            {isSevere && (
                              <button
                                onClick={() => toggleAlertExpand(i)}
                                className="text-xs text-teal-400 hover:text-teal-300 underline ml-1"
                              >
                                {isExpanded ? 'Hide VMs ▲' : '🔍 VMs ▼'}
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Active VMs sub-panel — critical / emergency only, when expanded */}
                        {isSevere && isExpanded && (
                          <div className="bg-black/30 border-t border-white/5 px-4 py-3">
                            <p className="text-xs text-teal-400 font-semibold mb-2">🔍 Active VMs at time of spike</p>
                            {Array.isArray(a.active_vms) && a.active_vms.length > 0 ? (
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-gray-500 border-b border-white/10">
                                    <th className="text-left py-1 pr-4">User</th>
                                    <th className="text-left py-1 pr-4">VM</th>
                                    <th className="text-left py-1 pr-4">Course</th>
                                    <th className="text-left py-1">Lab</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {a.active_vms.map((vm, vi) => (
                                    <tr key={vi} className="border-b border-white/5 hover:bg-white/5">
                                      <td className="py-1 pr-4 text-gray-300">{vm.user_email || '—'}</td>
                                      <td className="py-1 pr-4 text-gray-400 font-mono">{vm.vm_name || '—'}</td>
                                      <td className="py-1 pr-4 text-gray-400">{vm.course || '—'}</td>
                                      <td className="py-1 text-gray-400">{vm.lab_id || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <p className="text-gray-500 text-xs">No VMs were running at this time.</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-green-400 text-sm font-medium">System Healthy - No Active Alerts</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ========== VM AUDIT LOG VIEW ========== */}
      {view === 'audit' && (
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-white text-base flex items-center gap-2"><Server className="w-4 h-4 text-teal-400" />VM Audit Log</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-500" />
                  <input type="text" placeholder="Search by email..." value={auditSearch} onChange={e => setAuditSearch(e.target.value)}
                    className="bg-white/10 border border-white/10 rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder-gray-500 w-56" data-testid="audit-search" />
                </div>
                <Button size="sm" onClick={fetchAudit} className="bg-teal-600 text-white">Search</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {auditLogs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-400 border-b border-white/10">
                      <th className="text-left py-2 px-2">User</th>
                      <th className="text-left py-2 px-2">VM</th>
                      <th className="text-left py-2 px-2">IP</th>
                      <th className="text-left py-2 px-2">Course</th>
                      <th className="text-left py-2 px-2">Lab</th>
                      <th className="text-left py-2 px-2">Action</th>
                      <th className="text-left py-2 px-2">Started</th>
                      <th className="text-left py-2 px-2">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-2 px-2 text-gray-300">{log.user_email}</td>
                        <td className="py-2 px-2 text-gray-400 font-mono text-xs">{log.vm_name}</td>
                        <td className="py-2 px-2 text-gray-400 font-mono text-xs">{log.ip_address}</td>
                        <td className="py-2 px-2 text-gray-300">{log.course}</td>
                        <td className="py-2 px-2 text-gray-300">{log.lab_id}</td>
                        <td className="py-2 px-2">{getActionBadge(log.action)}</td>
                        <td className="py-2 px-2 text-gray-400 text-xs">{log.started_at ? new Date(log.started_at).toLocaleString('en-IN') : '-'}</td>
                        <td className="py-2 px-2 text-gray-400 text-xs">
                          {log.duration_seconds > 0 ? `${Math.round(log.duration_seconds / 60)}m` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-8">No VM audit logs yet. Logs will appear after first VM start/stop on production.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ========== CONFIG VIEW ========== */}
      {view === 'config' && editConfig && (
        <Card className="bg-white/5 border-white/10">
          <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Settings className="w-4 h-4 text-teal-400" />PPS Monitoring Config</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-400 block mb-1">Monitoring Enabled</label>
                <Button size="sm" onClick={() => setEditConfig(p => ({ ...p, enabled: !p.enabled }))}
                  className={editConfig.enabled ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}>
                  {editConfig.enabled ? 'ON' : 'OFF'}
                </Button>
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Auto-Kill on Emergency</label>
                <Button size="sm" onClick={() => setEditConfig(p => ({ ...p, auto_kill_enabled: !p.auto_kill_enabled }))}
                  className={editConfig.auto_kill_enabled ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}>
                  {editConfig.auto_kill_enabled ? 'ON' : 'OFF'}
                </Button>
              </div>
              {[
                { key: 'warning_threshold', label: 'Warning Threshold (PPS)', color: 'text-yellow-400' },
                { key: 'critical_threshold', label: 'Critical Threshold (PPS)', color: 'text-red-400' },
                { key: 'emergency_threshold', label: 'Emergency Threshold (PPS)', color: 'text-red-500' },
                { key: 'alert_cooldown_minutes', label: 'Alert Cooldown (minutes)', color: 'text-gray-300' },
              ].map(f => (
                <div key={f.key}>
                  <label className={`text-xs ${f.color} block mb-1`}>{f.label}</label>
                  <input type="number" value={editConfig[f.key] || ''} onChange={e => setEditConfig(p => ({ ...p, [f.key]: parseInt(e.target.value) || 0 }))}
                    className="w-full bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="text-xs text-gray-400 block mb-1">Email Recipients</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(editConfig.email_recipients || []).map((email, i) => (
                    <span key={i} className="flex items-center gap-1 bg-teal-600/20 border border-teal-500/30 rounded-full px-3 py-1 text-xs text-teal-300">
                      {email}
                      <button onClick={() => setEditConfig(p => ({ ...p, email_recipients: p.email_recipients.filter((_, j) => j !== i) }))}
                        className="text-red-400 hover:text-red-300 ml-1 font-bold">x</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input type="email" id="new-email-input" placeholder="Add email address..."
                    className="flex-1 bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        const val = e.target.value.trim().replace(/,/g, '');
                        if (val && val.includes('@') && !(editConfig.email_recipients || []).includes(val)) {
                          setEditConfig(p => ({ ...p, email_recipients: [...(p.email_recipients || []), val] }));
                          e.target.value = '';
                        }
                      }
                    }} />
                  <Button size="sm" onClick={() => {
                    const inp = document.getElementById('new-email-input');
                    const val = inp.value.trim().replace(/,/g, '');
                    if (val && val.includes('@') && !(editConfig.email_recipients || []).includes(val)) {
                      setEditConfig(p => ({ ...p, email_recipients: [...(p.email_recipients || []), val] }));
                      inp.value = '';
                    }
                  }} className="bg-teal-600 text-white">Add</Button>
                </div>
              </div>
            </div>
            <Button onClick={saveConfig} className="bg-teal-600 hover:bg-teal-700 text-white">Save Configuration</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
